import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import { Button } from "@mapmycustomers/ui";

import noRows from "@app/assets/list/no-rows.svg";
import { showGlobalCreateCompanyModal } from "@app/component/createEditEntity/CreateCompanyModal/store/actions";
import { showGlobalCreateDealModal } from "@app/component/createEditEntity/CreateDealModal/store/actions";
import { showGlobalCreatePersonModal } from "@app/component/createEditEntity/CreatePersonModal/store/actions";
import loggingService from "@app/util/logging";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./NoRows.module.scss";
import ResetButton from "./ResetButton";

interface Props {
  entityType?: EntityType;
  onShowCreateCompanyModal: () => void;
  onShowCreateDealModal: () => void;
  onShowCreatePersonModal: () => void;
  showCreateCta?: boolean;
  totalRecords: number;
}

const NoRows: React.FC<Props> = ({
  entityType,
  onShowCreateCompanyModal,
  onShowCreateDealModal,
  onShowCreatePersonModal,
  showCreateCta,
  totalRecords,
}) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerHeight, setContainerHeight] = useState<number>(0);
  const noRowsImgHeight = useMemo(
    () => containerHeight / (containerHeight > 300 ? 3 : 4),
    [containerHeight]
  );

  useEffect(
    () => {
      const wrapperElement = containerRef.current?.closest("div[ref='overlayWrapper']");
      try {
        const observer = new ResizeObserver(() => {
          setContainerHeight(+(wrapperElement?.clientHeight || 0));
        });

        if (wrapperElement) {
          observer.observe(wrapperElement);
        }

        return () => {
          observer.disconnect();
        };
      } catch (e) {
        loggingService.error("Error in NoRows", e);
      }
    },
    // disabling because we need to re-run this effect when ref changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerRef.current]
  );

  let addEntity;
  switch (entityType) {
    case EntityType.COMPANY:
      addEntity = onShowCreateCompanyModal;
      break;
    case EntityType.DEAL:
      addEntity = onShowCreateDealModal;
      break;
    case EntityType.PERSON:
      addEntity = onShowCreatePersonModal;
      break;
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <img alt="No data" src={noRows} style={{ height: noRowsImgHeight }} />
      <div className={cn(styles.title, { [styles.smallTitle]: noRowsImgHeight < 250 })}>
        <FormattedMessage
          id="grid.overlay.noRows.title"
          defaultMessage="Nothing to see here"
          description="Title of no data for company records"
        />
      </div>

      {totalRecords ? (
        <>
          <div className={styles.description}>
            <FormattedMessage
              id="grid.overlay.noRows.resetFilterDescription"
              defaultMessage="You’ve applied too many filters"
              description="Title of too many filters applied"
            />
          </div>
          <ResetButton />
        </>
      ) : entityType ? (
        <>
          <div className={styles.description}>
            {intl.formatMessage(
              {
                id: "grid.overlay.noRows.addEntityDescription",
                defaultMessage: "You don’t have any {entityType}",
                description: "Descriptive text for the No Rows overlay for grids",
              },
              {
                entityType: getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: true,
                }),
              }
            )}
          </div>
          {addEntity && showCreateCta && (
            <div className={styles.addEntityButton}>
              <Button onClick={addEntity} type="link">
                {intl.formatMessage(
                  {
                    id: "grid.overlay.noRows.addNewEntity",
                    defaultMessage: "Add your first {entityType}",
                    description: "Button text to add new entity for the No Rows overlay for grids",
                  },
                  {
                    entityType: getEntityTypeDisplayName(intl, entityType, {
                      lowercase: false,
                      plural: false,
                    }),
                  }
                )}
              </Button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

const mapDispatchToProps = {
  onShowCreateCompanyModal: showGlobalCreateCompanyModal,
  onShowCreateDealModal: showGlobalCreateDealModal,
  onShowCreatePersonModal: showGlobalCreatePersonModal,
};

export default connect(null, mapDispatchToProps)(NoRows);
